<script setup>
// Chọn tag nào thuộc một trang. Lưu là THAY nguyên danh sách của khoá `labels`;
// bỏ tick hết = xoá cấu hình riêng, trang quay về hiện tất cả tag.
// API kiểm quyền admin phía server, không dựa vào việc giấu nút ở đây.
//
// Phần tìm / chép từ trang khác nằm ở `BoChonMuc.vue`, dùng chung với màn mẫu
// trả lời theo trang (issue #148). Tag không có chủ đề nên không nhóm.
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import PageConfigsAPI, { loiApi } from 'dashboard/api/pageConfigs';
import { pageLabelsOf } from 'dashboard/helper/pageConfig';
import { demThayDoi } from 'dashboard/helper/bonboz/boChon';
import Button from 'dashboard/components-next/button/Button.vue';
import BoChonMuc from '../components/BoChonMuc.vue';

const props = defineProps({
  inbox: { type: Object, required: true },
  daChon: { type: Array, default: () => [] },
});
const emit = defineEmits(['close', 'saved']);

const { t } = useI18n();
const accountLabels = useMapGetter('labels/getLabels');

// Modal dựng lại mỗi lần mở, nên `daChon` lúc mở chính là bản đã lưu.
const goc = new Set(props.daChon);
const chon = ref(new Set(props.daChon));
const dangLuu = ref(false);
const loi = ref('');

const soDaChon = computed(() => chon.value.size);
const thayDoi = computed(() => demThayDoi(goc, chon.value));
const coThayDoi = computed(() => thayDoi.value.them + thayDoi.value.bo > 0);

// Khoá là TIÊU ĐỀ tag — cấu hình trang lưu theo tiêu đề, không theo id.
const mucTag = computed(() =>
  accountLabels.value.map(l => ({
    key: l.title,
    nhan: l.title,
    phu: l.description || '',
    mau: l.color,
  }))
);
const layCuaTrang = inboxId => new Set(pageLabelsOf(inboxId));

const luu = async () => {
  dangLuu.value = true;
  loi.value = '';
  try {
    const titles = [...chon.value];
    // Gửi TIÊU ĐỀ label (không phải id). Chỉ gửi khoá `labels` — server gộp nên
    // màu và mẫu trả lời của trang giữ nguyên, và trả về toàn bộ cấu hình sau
    // khi gộp; tiêu đề không tồn tại thì server im lặng bỏ qua.
    const { data } = await PageConfigsAPI.update(props.inbox.id, {
      labels: titles,
    });
    useAlert(
      titles.length
        ? t('BONBOZ.TAG_TRANG.SAVED', { n: titles.length })
        : t('BONBOZ.TAG_TRANG.CLEARED')
    );
    emit('saved', { inboxId: props.inbox.id, cauHinh: data });
  } catch (e) {
    loi.value = loiApi(e);
  } finally {
    dangLuu.value = false;
  }
};
</script>

<template>
  <woot-modal show size="medium" :on-close="() => emit('close')">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header
        :header-title="
          $t('BONBOZ.TAG_TRANG.TITLE', { name: inbox.name || `#${inbox.id}` })
        "
        :header-content="$t('BONBOZ.TAG_TRANG.MODAL_HINT')"
      />
      <div class="flex flex-col gap-3 p-8 pt-4">
        <p v-if="loi" class="m-0 text-sm text-n-ruby-11">{{ loi }}</p>

        <p
          v-if="!accountLabels.length"
          class="m-0 text-sm text-n-slate-11"
          data-testid="khong-co-tag"
        >
          {{ $t('BONBOZ.TAG_TRANG.MODAL_NO_LABELS') }}
        </p>
        <BoChonMuc
          v-else
          v-model="chon"
          :muc="mucTag"
          :goc="goc"
          :trang-hien-tai="inbox.id"
          :lay-cua-trang="layCuaTrang"
        />

        <div class="flex items-center justify-between gap-2">
          <!-- Bỏ hết tag là trang quay về HIỆN TẤT CẢ tag — ngược với điều người
               bấm "bỏ hết" thường nghĩ, nên tô hổ phách. -->
          <span
            class="text-sm"
            :class="
              !soDaChon && goc.size ? 'text-n-amber-11' : 'text-n-slate-11'
            "
          >
            {{
              soDaChon
                ? $t('BONBOZ.TAG_TRANG.SAVED', { n: soDaChon })
                : $t('BONBOZ.TAG_TRANG.ALL')
            }}
          </span>
          <div class="flex gap-2">
            <Button
              :label="$t('BONBOZ.TAG_TRANG.CANCEL')"
              size="sm"
              slate
              faded
              @click="emit('close')"
            />
            <Button
              :label="
                dangLuu
                  ? $t('BONBOZ.TAG_TRANG.SAVING')
                  : coThayDoi
                    ? $t('BONBOZ.BO_CHON.LUU_THAY_DOI', thayDoi)
                    : $t('BONBOZ.TAG_TRANG.SAVE')
              "
              size="sm"
              :is-loading="dangLuu"
              :disabled="!coThayDoi"
              @click="luu"
            />
          </div>
        </div>
      </div>
    </div>
  </woot-modal>
</template>
